import React, { useState, useEffect, useContext } from 'react';
import { AppBar, Toolbar, Typography, IconButton, Box, useTheme, Tooltip } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { UserContext } from '../auth/auth-context';
import PositionedMenu from './accountMenu';
import RegionSelector from './regionSelector';
import { useTranslation } from 'react-i18next';
import { StyledLink } from '../../assets/css/styledComponent';
import Logo from "../../assets/images/plany365.ico";
import HeaderSearch from '../../pages/customer/HeaderSearch';
import CategoriesList from '../common/categoriesList';
import { useTokenLoader } from '../../util/tokenLoader';
import { useIsMobile } from '../../util/utils';
import { WalletMenuItem } from '../tip/walletMenuItem';

const CustomerHeader = (props) => {
    const { providerUser } = useContext(UserContext);
    const location = useLocation();
    const [showNavbarSearch, setShowNavbarSearch] = useState(false);
    const [showCategoriesList, setShowCategoriesList] = useState(true);
    const theme = useTheme();
    const isMobile = useIsMobile();
    const { loading: tokenLoading, error: tokenError } = useTokenLoader();

    const scroller = document.querySelector("#main");

    const { t } = useTranslation();

    useEffect(() => {
        const handleScroll = () => {
            if (scroller?.scrollTop > 150) {
                setShowNavbarSearch(true);
                setShowCategoriesList(false); // Show categories when scrolling
            } else {
                setShowNavbarSearch(false);
                setShowCategoriesList(true); // Hide categories when not scrolling
            }
        };

        scroller?.addEventListener("scroll", handleScroll);

        return () => {
            if (location.pathname === '/') {
                scroller?.removeEventListener("scroll", handleScroll);
            }
        };
    }, [scroller, location.pathname]);

    const isHomePage = location.pathname === '/';

    return (
        <AppBar 
            position="fixed"
            style={{
                background:'#22374a',
                boxShadow: 'none',
                transition: "1s ease-in-out all",
            }}
        >
            {tokenLoading && <p>Loading ...</p>}
            <Box display="flex" flexDirection="column" width="100%">
                <Toolbar
                    sx={{
                        justifyContent: 'space-between', // Space between left and right sections
                        alignItems: 'center',            // Vertical alignment
                        pt: 2,                           // Padding top
                        pb: 2,                           // Padding bottom
                        flexDirection: 'row',            // Horizontal layout 
                        backgroundColor: '#22374a', // Semi-transparent dark overlay
                        gap: { xs: '0.5rem', sm: '2rem' },// Responsive gap
                        width: '100%',                   // Full width
                    }}
                >
                    {/* Left Column: Plany365 logo */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start', // Move logo to the left
                            gap: 0.5,
                            flex: '0 0 auto', // Prevent the logo from taking up too much space
                            marginLeft: isMobile ? '-2.0em' : '2.5em'
                        }}
                    >
                        <StyledLink href="/" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                            <img
                                src={Logo}
                                alt="Company Logo"
                                style={{
                                    width: '30px', // Reduced logo size
                                    height: '30px', // Re
                                    marginRight: '16px',
                                    marginBottom: '4px',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.2)', // Subtle shadow on logo
                                    transition: 'all 0.3s ease', // Smooth transition
                                }}
                                onMouseOver={(e) => e.target.style.boxShadow = '0 0 10px rgba(255, 255, 255, 0.6)'} // Hover glow effect
                                onMouseOut={(e) => e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.2)'} // Reset glow
                            />
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: '0.9rem', // Reduced font size for "Plany"
                                    fontWeight: 400,
                                    fontFamily: 'Roboto, Arial, sans-serif',
                                    color: '#ffffff',
                                    textDecoration: 'none',
                                    letterSpacing: '1px',
                                    lineHeight: 1.2,
                                    whiteSpace: 'nowrap', // Prevent text from wrapping
                                    marginLeft: '-9px', // Move "Plany" text further left
                                }}
                            >
                                <span style={{ fontSize: 'inherit' }}>Plany</span>
                                <span style={{ fontSize: '0.9rem', marginLeft: '0.1em' }}>365</span>
                            </Typography>
                        </StyledLink>
                    </Box>

                    {/* Center Column: HeaderSearch for larger screens */}
                    <Box
                        sx={{
                            flex: '1 1 auto',
                            display: { xs: 'none', sm: 'flex' },
                            justifyContent: 'center',
                            alignItems: 'center',
                            maxWidth: "800px",
                            marginBottom: { xs: '20px', sm: '0' },
                        }}
                    >
                        {(!isHomePage || showNavbarSearch) && <HeaderSearch />}
                    </Box>


                    {/* Right Column: User Account Menu, Region Selector, Manage Your Business, Login */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: { xs: '8px', sm: '12px' }, // Adjusted gap for small and large screens
                            flexDirection: 'row',
                            marginTop: { xs: '10px', sm: '0' },
                            flexWrap: 'nowrap', // Prevent wrapping of items
                            width: 'auto', // Make the right section use available space
                        }}
                    >
                        <WalletMenuItem />
                        {/* Log In / Sign Up */}
                        {!providerUser.userId && (
                            <StyledLink
                                href="/login"
                                sx={{
                                    whiteSpace: 'normal', // Allow text to wrap
                                    padding: '6px 12px',
                                    maxWidth: { xs: '100%', sm: '150px' }, // Allow it to fill the container on xs, and restrict width on sm
                                    width: 'auto', // Allow button to take up available space
                                    overflow: 'hidden', // Prevent text overflow
                                    textOverflow: 'ellipsis', // Add ellipsis if text overflows on larger screens
                                    marginBottom: { xs: '10px', sm: '0' }, // Add margin on small screens between buttons
                                    flexWrap: 'wrap', // Allow wrapping for small screens
                                }}
                            >
                                Log In / Sign Up
                            </StyledLink>

                        )}

                        {/* Manage Business Button */}
                        {providerUser.defaultBusinessId ? (
                            <StyledLink
                                href="/biz/bookingtypes"
                                sx={{
                                    backgroundColor: theme.palette.background.paper,
                                    color: theme.palette.text.primary,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '10px',
                                    flexDirection: 'row',
                                    padding: '6px 12px',
                                    maxWidth: { xs: '100%', sm: '250px' }, // Allow it to fill the container on xs, and restrict width on sm
                                    width: 'auto', // Allow the button to take up available space
                                    overflow: 'hidden', // Prevent text overflow
                                    textOverflow: 'ellipsis', // Add ellipsis if text overflows
                                    marginBottom: { xs: '10px', sm: '0' }, // Space between buttons on small screens
                                    flexWrap: 'wrap', // Allow wrapping on small devices
                                }}
                            >
                                {t('manage')}
                            </StyledLink>
                        ) : ( 
                        <Tooltip
                            title={
                                <Typography sx={{ color: "#fff", fontSize: "0.875rem" }}>
                                            Create a business profile using your business name ( or your personal name if you don’t have one ) to manage services, events, classes, and related appointments.
                                </Typography>
                            }
                            arrow
                            placement="bottom"
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        backgroundColor: "#000", // black background
                                        maxWidth: 300,
                                        padding: "8px 12px",
                                    },
                                },
                            }}
                                > 
                            <StyledLink
                                        href="/biz"
                                sx={{
                                    whiteSpace: 'normal', // Allow text to wrap
                                    padding: '6px 12px',
                                    maxWidth: '100%', // Allow it to fill the container on xs, and restrict width on sm
                                    width: 'auto', // Allow button to take up available space
                                    overflow: 'hidden', // Prevent text overflow
                                    textOverflow: 'ellipsis', // Add ellipsis if text overflows on larger screens
                                    marginBottom: { xs: '10px', sm: '0' }, // Add margin on small screens between buttons
                                    flexWrap: 'wrap', // Allow wrapping for small screens
                                }}
                            >
                                        Create a Business Profile
                            </StyledLink>


                        </Tooltip>


                        )}
                        {/* User Account Menu */}
                        {providerUser.userId && (
                            <IconButton
                            edge="end"
                            color="inherit"
                            aria-label="account of current user"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            >
                                <PositionedMenu menu="Customer" />
                            </IconButton>
                        )}

                        {/* Region Selector */}
                        <RegionSelector />
                    </Box>
                </Toolbar>

                {/* Search and Categories container */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        minHeight: 'auto',
                        position: 'relative',
                        zIndex: 10,
                        overflow: 'visible',
                        top: { xs: '0', sm: 'auto' },
                    }}
                >
                    {/* Header Search Bar for smaller screens */}
                    {(!isHomePage || showNavbarSearch) && (
                        <Box
                            sx={{
                                display: { xs: 'block', sm: 'none' },
                                width: '100%',
                                padding: '1px',
                                zIndex: 11,
                            }}
                        >
                            <HeaderSearch />
                        </Box>
                    )}

                    {/* Categories List */}
                    {!isHomePage && showCategoriesList && (
                        <Box
                            sx={{
                                position: 'relative',
                                width: '100%',
                                marginTop: { xs: '4px', sm: '8px' },
                                zIndex: 11,
                                maxHeight: { xs: 'none', sm: '50vh' },
                                overflowY: 'auto',
                            }}
                        >
                            <CategoriesList />
                        </Box>
                    )}
                </Box>
            </Box>
        </AppBar>
    );
};

export default CustomerHeader;
